import ms from "ms";
import type { StringValue } from "ms";
import { Nullish } from "./types";
import { MimeTypeToExtension, MimeType } from "./mime";

export function formatISO(date: Date = new Date()): string {
    return date.toISOString();
}

export const isDefined = (value: any) => value !== undefined && value !== null;
export const isNotDefined = (value: any) => !isDefined(value);

export function createPredicatePair<T extends (...args: any[]) => boolean>(
    predicate: T,
): {
    positive: (...args: Parameters<T>) => boolean;
    negative: (...args: Parameters<T>) => boolean;
} {
    return {
        positive: predicate,
        negative: (...args: Parameters<T>) => !predicate(...args),
    };
}

export const { positive: isObjectEmpty, negative: isObjectNotEmpty } =
    createPredicatePair((obj: any) => {
        if (isNotDefined(obj)) {
            return true;
        }

        for (const prop in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                return false;
            }
        }

        return true;
    });

export const isError = (error: unknown): error is Error =>
    error instanceof Error;

export const isServer = () => typeof window === "undefined";

export const delay = (msDelay: number) =>
    new Promise((res) => setTimeout(res, msDelay));

export const createFullName = (firstName?: string, lastName?: string) => {
    let fullName = "";
    if (isDefined(firstName)) {
        fullName += firstName;
    }
    if (isDefined(lastName)) {
        if (fullName.length > 0) {
            fullName += " ";
        }
        fullName += lastName;
    }
    return fullName;
};

export const convertSecsToMilliSecs = (secs: number) => secs * 1000;

export const calculateExpirationDate = (duration: StringValue) =>
    new Date(Date.now() + ms(duration));

export const normalizeName = (name: Nullish<string>) => {
    const s = String(name ?? "").trim();

    // Convert curly/smart apostrophes to straight apostrophe
    const replacedUnicodeApos = s.replace(/[\u2018\u2019\u201B\u2032]/g, "'");

    // Collapse whitespace, apostrophes and hyphens; then remove spaces around apostrophes/hyphens
    return replacedUnicodeApos
        .replace(/\s+/g, " ")
        .replace(/'+/g, "'")
        .replace(/-+/g, "-")
        .replace(/\s*'\s*/g, "'")
        .replace(/\s*-\s*/g, "-");
};

export const calculateSkip = (page: number, limit: number) =>
    (page - 1) * limit;

export const calculateTotalPages = (totalItems: number, limit: number) =>
    Math.max(1, Math.ceil(totalItems / limit));

export const createPaginationMeta = (
    totalItems: number,
    page: number,
    limit: number,
) => ({
    total: totalItems,
    page,
    lastPage: Math.max(1, Math.ceil(totalItems / limit)),
    limit,
    totalPages: calculateTotalPages(totalItems, limit),
});

export const chunk = <T>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    const chunkSize = size > 0 ? size : 1;

    for (let i = 0; i < arr.length; i += chunkSize) {
        out.push(arr.slice(i, i + chunkSize));
    }
    return out;
};

export function formatSecondsToMMSS(seconds: Nullish<number>): string {
    if (seconds === undefined || seconds === null) {
        return "—";
    }
    const s = Math.max(0, Math.round(seconds));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
}

export function createFilenameWithExtension(
    filename: string,
    mimeType?: MimeType | null,
) {
    const name = (filename ?? "").trim() || "unknown";

    // preserve any directory part and operate only on the final segment
    const lastSep = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
    const dir = lastSep >= 0 ? name.slice(0, lastSep + 1) : "";
    const base = lastSep >= 0 ? name.slice(lastSep + 1) : name;

    // derive extension from known mapping, otherwise use subtype or 'bin'
    let ext: string | undefined;
    if (mimeType) {
        ext = MimeTypeToExtension[mimeType];
    }

    const nameWithoutExt = base.replace(/\.[^/.]+$/, "");
    return `${dir}${nameWithoutExt}.${ext ?? "bin"}`;
}
